import { Component, createRef } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

type LibState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; toIco: any }
    | { status: 'error'; message: string };

interface State {
    sourceDataUri: string;
    sourceFileName: string;
    error: string | null;
    bgColor: string;
    rounded: boolean;
    busy: boolean;
    lib: LibState;
    icoUrl: string | null;
    pngBlobs: Map<number, Blob>;
}

const ICO_SIZES = [16, 32, 48];
const PWA_SIZES = [16, 32, 48, 180, 192, 512];

export default class FaviconGeneratorComponent extends Component<{ feature?: string }, State> {
    private previewRef = createRef<HTMLCanvasElement>();
    state: State = {
        sourceDataUri: '',
        sourceFileName: '',
        error: null,
        bgColor: '#ffffff',
        rounded: false,
        busy: false,
        lib: { status: 'idle' },
        icoUrl: null,
        pngBlobs: new Map(),
    };

    private async ensureLibLoaded() {
        if (this.state.lib.status === 'ready') return this.state.lib.toIco;
        if (this.state.lib.status === 'loading') return null;
        this.setState({ lib: { status: 'loading' } });
        try {
            const mod: any = await import('to-ico');
            const toIco = mod.default || mod;
            this.setState({ lib: { status: 'ready', toIco } });
            return toIco;
        } catch (e) {
            this.setState({ lib: { status: 'error', message: 'Failed to load ICO encoder. Check your connection and refresh.' } });
            return null;
        }
    }

    handleFile = (file: File) => {
        if (!file.type.startsWith('image/')) {
            this.setState({ error: `Unsupported file type: ${file.type || 'unknown'}. Please upload an image.` });
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            this.setState({
                sourceDataUri: String(reader.result),
                sourceFileName: file.name,
                error: null,
                icoUrl: null,
                pngBlobs: new Map(),
            }, () => this.renderPreview());
        };
        reader.onerror = () => this.setState({ error: 'Failed to read file. Please try again.' });
        reader.readAsDataURL(file);
    };

    onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) this.handleFile(f);
    };

    onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) this.handleFile(f);
    };

    private async renderPreview() {
        const canvas = this.previewRef.current;
        if (!canvas || !this.state.sourceDataUri) return;
        const img = new Image();
        await new Promise<void>((res, rej) => {
            img.onload = () => res();
            img.onerror = () => rej(new Error('image load failed'));
            img.src = this.state.sourceDataUri;
        });
        this.drawSize(canvas, img, 64);
    }

    private async drawSize(canvas: HTMLCanvasElement, img: HTMLImageElement, size: number) {
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, size, size);
        if (this.state.rounded) {
            ctx.save();
            ctx.beginPath();
            const r = size * 0.2;
            ctx.moveTo(r, 0);
            ctx.arcTo(size, 0, size, size, r);
            ctx.arcTo(size, size, 0, size, r);
            ctx.arcTo(0, size, 0, 0, r);
            ctx.arcTo(0, 0, size, 0, r);
            ctx.closePath();
            ctx.clip();
        }
        ctx.fillStyle = this.state.bgColor;
        ctx.fillRect(0, 0, size, size);
        // contain-fit
        const ar = img.width / img.height;
        let dw = size, dh = size;
        if (ar > 1) dh = size / ar;
        else dw = size * ar;
        const dx = (size - dw) / 2;
        const dy = (size - dh) / 2;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, dx, dy, dw, dh);
        if (this.state.rounded) ctx.restore();
    }

    private async sizeToBlob(img: HTMLImageElement, size: number): Promise<Blob> {
        const c = document.createElement('canvas');
        await this.drawSize(c, img, size);
        return new Promise<Blob>((res, rej) => {
            c.toBlob(b => b ? res(b) : rej(new Error('blob failed')), 'image/png');
        });
    }

    generate = async () => {
        if (!this.state.sourceDataUri) {
            this.setState({ error: 'Upload an image first.' });
            return;
        }
        const toIco = await this.ensureLibLoaded();
        if (!toIco) return;
        this.setState({ busy: true, error: null });
        try {
            const img = new Image();
            await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error('image load failed')); img.src = this.state.sourceDataUri; });

            // Generate PNG blobs at all sizes
            const pngBlobs = new Map<number, Blob>();
            for (const size of PWA_SIZES) {
                pngBlobs.set(size, await this.sizeToBlob(img, size));
            }

            // Build .ico from the small sizes (16/32/48)
            const buffers = await Promise.all(ICO_SIZES.map(s => pngBlobs.get(s)!.arrayBuffer().then(b => Buffer.from(b))));
            const icoBuf = await toIco(buffers);
            const icoBlob = new Blob([icoBuf], { type: 'image/x-icon' });
            const icoUrl = URL.createObjectURL(icoBlob);

            this.setState({ pngBlobs, icoUrl, busy: false });
        } catch (e: any) {
            this.setState({ busy: false, error: e?.message || 'Failed to generate favicon.' });
        }
    };

    downloadPng = (size: number) => {
        const blob = this.state.pngBlobs.get(size);
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `favicon-${size}x${size}.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    downloadIco = () => {
        if (!this.state.icoUrl) return;
        const a = document.createElement('a');
        a.href = this.state.icoUrl;
        a.download = 'favicon.ico';
        a.click();
    };

    componentWillUnmount() {
        if (this.state.icoUrl) URL.revokeObjectURL(this.state.icoUrl);
    }

    componentDidUpdate(_p: any, prev: State) {
        if (prev.bgColor !== this.state.bgColor || prev.rounded !== this.state.rounded) {
            this.renderPreview();
            // Invalidate previously generated artifacts
            if (this.state.icoUrl) {
                URL.revokeObjectURL(this.state.icoUrl);
                this.setState({ icoUrl: null, pngBlobs: new Map() });
            }
        }
    }

    render() {
        const { sourceDataUri, sourceFileName, error, bgColor, rounded, busy, lib, icoUrl, pngBlobs } = this.state;
        const buttonText =
            lib.status === 'loading' ? 'Loading…' :
            busy ? 'Generating…' :
            '⚙ Generate favicon set';
        const buttonDisabled = !sourceDataUri || busy || lib.status === 'loading';

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Source image</span></div>
                    <div
                        onDragOver={e => e.preventDefault()}
                        onDrop={this.onDrop}
                        style={{ border: '2px dashed #cbd5e1', borderRadius: 8, padding: '1.25rem', textAlign: 'center', cursor: 'pointer', background: '#f8fafc' }}
                    >
                        <input type="file" id="favicon-source" accept="image/*"
                            onChange={this.onInputChange} style={{ display: 'none' }} />
                        <label htmlFor="favicon-source" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            {sourceDataUri ? <>
                                <canvas ref={this.previewRef} style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 8 }} />
                                <span style={{ fontSize: '0.85rem', color: '#1e293b', fontWeight: 600 }}>{sourceFileName}</span>
                            </> : <>
                                <span style={{ fontSize: '2.5rem', opacity: 0.4 }}>⇪</span>
                                <span style={{ color: '#1e293b' }}>Drop image, or click to choose</span>
                                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Square PNG / JPG / SVG (≥ 512px recommended)</span>
                            </>}
                        </label>
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="fav-bg">Background</label>
                        <input id="fav-bg" type="color" value={bgColor} style={{ height: 38, padding: 4 }}
                            onChange={e => this.setState({ bgColor: e.target.value })} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={rounded} onChange={e => this.setState({ rounded: e.target.checked })} />
                        Rounded corners (iOS-style)
                    </label>
                    <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate}
                        disabled={buttonDisabled} style={{ alignSelf: 'flex-start' }}>
                        {buttonText}
                    </button>
                </div>
            </>
        );

        const preview = icoUrl ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem', width: '100%' }}>
                <div style={{ border: '1px solid #6366f1', background: '#eef2ff', borderRadius: 8, padding: '0.75rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.78rem', color: '#4338ca', fontWeight: 600 }}>favicon.ico</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>16/32/48</div>
                    <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.downloadIco}
                        style={{ marginTop: '0.5rem', width: '100%' }}>
                        ⬇ .ico
                    </button>
                </div>
                {Array.from(pngBlobs.keys()).sort((a, b) => a - b).map(size => (
                    <div key={size} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '0.75rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.78rem', color: '#1e293b', fontWeight: 600 }}>{size}×{size}</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>PNG</div>
                        <button className={genStyles.iconBtn} onClick={() => this.downloadPng(size)}
                            style={{ marginTop: '0.5rem', width: '100%' }}>
                            ⬇
                        </button>
                    </div>
                ))}
            </div>
        ) : (
            <div style={{ color: '#64748b', textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>★</div>
                <div style={{ marginTop: '0.5rem' }}>Upload an image and click Generate to produce a multi-size .ico plus PNGs for iOS, Android, and PWA installs.</div>
            </div>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Generated favicon set"
                previewMeta={icoUrl ? `${pngBlobs.size + 1} files` : undefined}
                preview={preview}
                codeLabel={'HTML to add to <head>'}
                code={`<link rel="icon" href="/favicon.ico" sizes="any">
<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">
<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
<link rel="apple-touch-icon" sizes="180x180" href="/favicon-180x180.png">
<link rel="manifest" href="/site.webmanifest">`}
                codeActions={
                    <button className={genStyles.iconBtn}
                        onClick={() => navigator.clipboard.writeText('<link rel="icon" href="/favicon.ico" sizes="any">\n<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">\n<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">\n<link rel="apple-touch-icon" sizes="180x180" href="/favicon-180x180.png">\n<link rel="manifest" href="/site.webmanifest">')}>
                        📋 Copy HTML
                    </button>
                }
            />
        );
    }
}
